import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import DayPickerInput from 'react-day-picker/DayPickerInput';

import TextInput from '../TextInput';
import { isFunction, isString } from 'lib/javascript';
import datetime, {
  DATE_FORMAT,
  switchTimezoneButKeepLocalTime,
  getClientTimezone,
} from 'lib/datetime';

import 'react-day-picker/lib/style.css';
import classNames from 'react-day-picker/build/classNames';
import cssModule from './DatePicker.module.css';

function YearMonthForm({
  date,
  localeUtils,
  onChange,
  fromMonth,
  toMonth,
}) {
  const months = localeUtils.getMonths();

  const years = [];
  let fromYear = 1901;
  let toYear = datetime().add(20, 'year').year();
  if (fromMonth) {
    fromYear = datetime(fromMonth).year();
  }
  if (toMonth) {
    toYear = datetime(toMonth).year();
  }

  for (let i = fromYear; i <= toYear; i += 1) {
    years.push(i);
  }

  const handleChange = function handleChange(e) {
    const { year, month } = e.target.form;
    onChange(new Date(year.value, month.value));
  };

  return (
    <form
      className="DayPicker-Caption tw-mb-0"
      style={{ padding: 0, marginRight: '30px' }}
    >
      <select
        name="month"
        onChange={handleChange}
        value={date.getMonth()}
        className="tw-text-[1.4rem]"
      >
        {months.map((month, i) => (
          <option key={month} value={i}>
            {month}
          </option>
        ))}
      </select>
      <select
        name="year"
        onChange={handleChange}
        value={date.getFullYear()}
        className="tw-text-[1.4rem]"
      >
        {years.map((year) => (
          <option key={year} value={year}>
            {year}
          </option>
        ))}
      </select>
    </form>
  );
}

const DatePicker = ({
  value,
  onChange,
  disabled,
  disabledDays,
  fromMonth,
  toMonth,
  label,
  helperText,
  error,
  outputFormat,
  styles,
  timezone,
  showOverlayDateFormat = false,
  placeholder,
  onBlur,
  onMonthChange,
  name,
  eventDates = [],
}) => {
  const inputRef = useRef(null);
  const [currentMonth, setCurrentMonth] = useState(
    value ? datetime(value).toDate() : null,
  );

  const getValue = () => {
    if (timezone)
      return switchTimezoneButKeepLocalTime(value, {
        fromTimezone: timezone,
        toTimezone: getClientTimezone(),
      }).toDate();

    return value;
  };

  const handleOnDayChange = (day, _modifiers, _dayPickerInput) => {
    const raw = timezone
      ? switchTimezoneButKeepLocalTime(day, {
          fromTimezone: getClientTimezone(),
          toTimezone: timezone,
        }).toDate()
      : day;

    const formattedDay = outputFormat
      ? formatDate(raw, outputFormat) // if there's a specific output format, format to the required format date string.
      : raw; // otherwise leave it as Date object

    onChange(formattedDay);
  };

  const handleYearMonthChange = (m) => {
    setCurrentMonth(m);
    onMonthChange?.(m);
  };

  const parseDate = (str, format, _locale) => {
    const parsed = datetime(str, format);

    if (!parsed.isValid()) return undefined;

    return parsed.toDate();
  };

  const formatDate = (date, format, _locale) => {
    if (isFunction(format)) return format(date);

    if (isString(format)) return datetime(date).format(format);

    throw new Error('format must be either string or function');
  };

  const getDisabledDays = (day) => {
    if (!disabledDays) return false;

    // For convenient's sake, only support function type 'disabledDays' for now
    // TODO: support all supported 'disabledDays' prop type as detailed on the official react-day-picker documentation
    // https://react-day-picker.js.org/docs/matching-days
    if (!isFunction(disabledDays))
      throw new Error(
        "DatePicker component's 'disabledDays' prop must be a function. ",
      );

    return disabledDays(
      timezone
        ? switchTimezoneButKeepLocalTime(day, {
            fromTimezone: getClientTimezone(),
            toTimezone: timezone,
          })
        : day,
    );
  };

  const handleOnBlur = () => {
    if (onBlur && typeof onBlur === 'function') {
      onBlur({ target: { name } });
    }
  };

  return (
    <DayPickerInput
      format={DATE_FORMAT}
      placeholder={placeholder}
      formatDate={formatDate}
      parseDate={parseDate}
      value={getValue()}
      onDayChange={handleOnDayChange}
      onDayPickerHide={handleOnBlur}
      component={(props) => (
        <div className="position-relative">
          <TextInput
            {...props}
            className={
              showOverlayDateFormat
                ? cssModule.inputOverlay
                : cssModule.input
            }
            labelProps={{ style: styles.label || {} }}
            formGroupProps={{ style: styles.formGroup || {} }}
            helperProps={{ style: styles.helperText || {} }}
            label={label}
            helperText={helperText}
            error={error}
            name={name}
            ref={inputRef}
          />
          {showOverlayDateFormat && (
            <div
              className={cssModule.formatSelectedDate}
              onClick={() => inputRef.current.focus()}
            >
              {formatDate(value, 'DD MMM, dddd')}
            </div>
          )}
        </div>
      )}
      inputProps={{ readOnly: true, disabled }}
      dayPickerProps={{
        disabledDays: getDisabledDays,
        month: currentMonth,
        fromMonth,
        toMonth,
        onMonthChange: handleYearMonthChange,
        captionElement: ({ date, localeUtils }) => (
          <YearMonthForm
            date={date}
            localeUtils={localeUtils}
            onChange={handleYearMonthChange}
            fromMonth={fromMonth}
            toMonth={toMonth}
          />
        ),
        renderDay: (date) => {
          const event = eventDates.find((d) => {
            if (datetime(d.date).isSame(date, 'day')) {
              return d;
            }

            return null;
          });

          return (
            <div className="tw-flex tw-flex-col tw-w-full">
              {date.getDate()}
              {event && (
                <div className="tw-flex tw-justify-center tw-gap-1 tw-mt-1">
                  {/* <span
                    className={`tw-text-[8px] tw-font-semibold tw-text-[#999] ${cssModule.DayPickerEvent}`}
                  >
                    {event.count} {pluralize(event.count, 'time')}
                  </span> */}
                  {Array.from({ length: event.count }, (_, i) => (
                    <span
                      key={i}
                      className={`tw-w-2 tw-h-2 tw-rounded-full ${cssModule.DayPickerEvent}`}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        },
        modifiers: eventDates.length
          ? {
              availableSlotsCount: eventDates,
            }
          : undefined,
        classNames: {
          ...classNames,
          container: `DayPicker ${cssModule.DayPicker}`,
          caption: 'Fak',
          weekdaysRow: `DayPicker-Weekdays tw-flex tw-w-full tw-mt-0`,
          weekdays: 'DayPicker-Weekdays tw-mt-0',
          weekday: `DayPicker-Weekday tw-flex-1 tw-aspect-square tw-flex tw-items-center tw-justify-center tw-p-0 tw-text-[1.4rem]`,
          month: `DayPicker-Month ${cssModule.DayPickerMonth}`,
          selected: `DayPicker-Day--selected ${cssModule.DayPickerDaySelected}`,
          today: 'DayPicker-Day--today',
          todayButton: 'DayPicker-TodayButton',
          outside: 'DayPicker-Day--outside',
          disabled: 'DayPicker-Day--disabled',
          body: 'DayPicker-Body tw-space-y-1',
          week: `DayPicker-Week tw-flex tw-w-full tw-gap-1`,
          day: `DayPicker-Day tw-flex-1 tw-aspect-square tw-flex tw-items-center tw-justify-center tw-p-0 tw-text-[1.4rem]`,
        },
      }}
      classNames={{
        overlay: `DayPickerInput--Overlay ${cssModule.overlay}`,
        // reassign the defaults because custom className is not merge with defaults
        overlayWrapper: 'DayPickerInput-OverlayWrapper',
      }}
    />
  );
};

DatePicker.propTypes = {
  value: PropTypes.instanceOf(Date),
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  disabledDays: PropTypes.func,
  fromMonth: PropTypes.instanceOf(Date),
  toMonth: PropTypes.instanceOf(Date),
  label: PropTypes.string,
  helperText: PropTypes.string,
  error: PropTypes.bool,
  outputFormat: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func,
  ]),
  timezone: PropTypes.string,
  styles: PropTypes.shape({
    label: PropTypes.object,
    formGroup: PropTypes.object,
    helperText: PropTypes.object,
  }),
  placeholder: PropTypes.string,
  onBlur: PropTypes.func,
  onMonthChange: PropTypes.func,
  name: PropTypes.string,
};

DatePicker.defaultProps = {
  onChange: () => {},
  styles: {},
};

export default DatePicker;
